import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { PERMISSION } from 'src/features/auth';
import { PATH } from 'src/features/navigation/utils/constants';
import { useGetPayStationEvent } from 'src/features/payment/api/useGetPayStationEvent';
import EventMetaDataMap from 'src/features/payment/components/EventMetaDataMap';
import {
    eventAPIBadgeOptions,
    eventCategoryBadgeOptions,
    EventType,
    eventTypeTranslationMap,
    payStationTypeBadgeOptions,
} from 'src/features/payment/utils/constants';
import { paymentsMessages } from 'src/features/payment/utils/messages';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { useParking } from 'src/providers/ParkingProvider';
import { LinkWithIcon } from 'src/registry/components/navigation/LinkWithIcon';
import {
    SidePanelBodyAttribute,
    SidePanelBodyAttributeBody,
    SidePanelBodyAttributeTitle,
} from 'src/registry/components/sidePanel/SidePanelBody';
import { Badge } from 'src/registry/ui/Badge';
import { Button } from 'src/registry/ui/Button';
import {
    DialogBody,
    DialogClose,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
} from 'src/registry/ui/Dialog';
import { Skeleton } from 'src/registry/ui/Skeleton';
import { generalMessages } from 'src/registry/utils/messages';
import { getFormattedDateTimeForDetailPanel } from 'src/utils/getFormattedDateTime';

type ActiveStaysSidePanelProps = {
    eventId?: string;
};

export default function EventSidePanel({ eventId }: ActiveStaysSidePanelProps) {
    const { formatMessage } = useIntl();
    const { payStationId = '' } = useParams();

    const { parkingId } = useParking();
    const { data } = useGetPayStationEvent({
        parkingId,
        eventId: eventId ?? '',
        config: {
            enabled: !!eventId,
        },
    });

    const accessPayStations = useHasPermissions([PERMISSION.ACCESS_PAY_STATIONS]);
    const payStationURL = data?.pay_station.id ? `/${PATH.PAYMENTS}/pay-station/${data?.pay_station.id}` : '';

    return (
        <>
            <DialogHeader>
                {data ? (
                    <>
                        <div className="flex items-center gap-2">
                            <DialogTitle className="pr-0 font-mono">
                                {formatMessage(paymentsMessages.eventLog)}
                            </DialogTitle>
                            <Badge theme={eventCategoryBadgeOptions[data.category].theme}>
                                {formatMessage(eventCategoryBadgeOptions[data.category].label)}
                            </Badge>
                        </div>
                        <DialogDescription>{data?.pay_station.name}</DialogDescription>
                    </>
                ) : (
                    <>
                        <Skeleton className="h-7 w-32" />
                        <Skeleton className="h-5 w-60" />
                    </>
                )}
            </DialogHeader>
            <DialogSeparator />
            <DialogBody>
                <div className="grid gap-4 sm:gap-6">
                    <SidePanelBodyAttribute>
                        <SidePanelBodyAttributeTitle>
                            {formatMessage(paymentsMessages.date)}
                        </SidePanelBodyAttributeTitle>
                        <SidePanelBodyAttributeBody control>
                            {getFormattedDateTimeForDetailPanel(data?.created_at)}
                        </SidePanelBodyAttributeBody>
                    </SidePanelBodyAttribute>
                    <SidePanelBodyAttribute>
                        <SidePanelBodyAttributeTitle>
                            {formatMessage(paymentsMessages.payStation)}
                        </SidePanelBodyAttributeTitle>
                        <SidePanelBodyAttributeBody control>
                            <LinkWithIcon
                                to={payStationURL}
                                linkTitle={data?.pay_station.name ?? '-'}
                                hasPermissions={accessPayStations && !payStationId}
                            />
                        </SidePanelBodyAttributeBody>
                    </SidePanelBodyAttribute>
                    <SidePanelBodyAttribute>
                        <SidePanelBodyAttributeTitle>
                            {formatMessage(paymentsMessages.type)}
                        </SidePanelBodyAttributeTitle>
                        <SidePanelBodyAttributeBody control>
                            {data?.type && eventTypeTranslationMap[data.type]
                                ? formatMessage(eventTypeTranslationMap[data?.type])
                                : data?.type}
                        </SidePanelBodyAttributeBody>
                    </SidePanelBodyAttribute>
                    <SidePanelBodyAttribute>
                        <SidePanelBodyAttributeTitle>
                            {formatMessage(paymentsMessages.category)}
                        </SidePanelBodyAttributeTitle>
                        <SidePanelBodyAttributeBody control>
                            {data?.category ? (
                                <Badge theme={eventCategoryBadgeOptions[data.category].theme}>
                                    {formatMessage(eventCategoryBadgeOptions[data.category].label)}
                                </Badge>
                            ) : (
                                '-'
                            )}
                        </SidePanelBodyAttributeBody>
                    </SidePanelBodyAttribute>
                    <SidePanelBodyAttribute>
                        <SidePanelBodyAttributeTitle>
                            {formatMessage(paymentsMessages.receivedFrom)}
                        </SidePanelBodyAttributeTitle>
                        <SidePanelBodyAttributeBody control>
                            {data?.received_from ? (
                                <Badge theme={eventAPIBadgeOptions[data.received_from].theme}>
                                    {formatMessage(eventAPIBadgeOptions[data.received_from].label)}
                                </Badge>
                            ) : (
                                '-'
                            )}
                        </SidePanelBodyAttributeBody>
                    </SidePanelBodyAttribute>
                    <SidePanelBodyAttribute>
                        <SidePanelBodyAttributeTitle>
                            {formatMessage(paymentsMessages.operation)}
                        </SidePanelBodyAttributeTitle>
                        <SidePanelBodyAttributeBody control>
                            {data?.paystation_operation?.type ? (
                                <Badge theme={payStationTypeBadgeOptions[data.paystation_operation.type].theme}>
                                    {formatMessage(payStationTypeBadgeOptions[data.paystation_operation.type].label)}
                                </Badge>
                            ) : (
                                '-'
                            )}
                        </SidePanelBodyAttributeBody>
                    </SidePanelBodyAttribute>
                    {data && (
                        <>
                            {data.type !== EventType.CANCEL_OPERATION &&
                                data.type !== EventType.LIST_ACTIVE_STAY &&
                                data.type !== EventType.RETRIEVE_ACTIVE_STAY &&
                                data.type > EventType.UNKNOWN_EVENT && <DialogSeparator className="m-4" />}
                            <EventMetaDataMap type={data.type} meta={data.meta} />
                        </>
                    )}
                </div>
            </DialogBody>
            <DialogFooter>
                <DialogClose asChild>
                    <Button variant="outline" className="flex-1">
                        {formatMessage(generalMessages.close)}
                    </Button>
                </DialogClose>
            </DialogFooter>
        </>
    );
}
